import { CheckCircleIcon, PencilIcon, XCircleIcon } from '@heroicons/react/24/solid';
import React, { useState, useEffect, useMemo } from 'react';
import { Spinner } from '../../utils/Spinner';
import VetRecButton from './VetRecButton';

interface EditableLabelProps {
    text: string | undefined;
    onSave: (newText: string) => Promise<void>;
    className?: string;
    textInputPlaceholder?: string;
    labelPlaceholder?: string;
    disabled?: boolean;
    onEditingChange?: (editing: boolean) => void;
}

const EditableLabel: React.FC<EditableLabelProps> = ({
    text,
    onSave,
    className = '',
    textInputPlaceholder = 'Enter text...',
    labelPlaceholder='Not specified',
    disabled = false,
    onEditingChange,
}) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState<string | undefined>(undefined);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setValue(text);

        if (text) {
            setEditing(false);
        } else {
            setEditing(true);
        }
    }, [text]);

    useEffect(() => {
        if (disabled) {
            setEditing(false);
        }
    }, [disabled]);

    function enableEditing(e: React.MouseEvent) {
        e.stopPropagation();
        if (onEditingChange) onEditingChange(true);
        setEditing(true);
    }

    function disableEditing(e: React.MouseEvent) {
        e.stopPropagation();
        if (onEditingChange) onEditingChange(false);
        setEditing(false);
    }

    async function handleSaveClick(e: React.MouseEvent) {
        e.stopPropagation();
        if (!disabled && value) {
            setIsSaving(true);
            try {
                await onSave(value);
                disableEditing(e);
            } finally {
                setIsSaving(false);
            }
        }
    }

    const disableButtons = useMemo(() => isSaving || !value, [value, isSaving]);

    return (
        <div className={`flex flex-row gap-x-2 justify-center items-center ${className}`}>
            {!editing && <span className={value !== undefined ? "text-gray-700" : "text-gray-400"}>{value ?? labelPlaceholder}</span>}
            {!editing && !disabled && <div
                className="flex flex-row rounded-full hover:bg-selected h-6 w-6 items-center justify-center cursor-pointer"
                onClick={enableEditing}
            >
                <PencilIcon className="h-4 w-4" />
            </div>}
            {editing && <input
                type="text"
                name="name"
                id="name"
                className="block rounded-md border-0 py-1.5 text-main-text-darker shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 mr-2"
                placeholder={textInputPlaceholder}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                disabled={isSaving}
                onClick={(e) => e.stopPropagation()}
            />}
            {editing && !disabled && <VetRecButton
                className={`flex flex-row rounded-full ${isSaving || disableButtons ? '' : 'hover:text-green-600'} h-6 w-6 items-center justify-center ${isSaving ? 'cursor-wait' : (disableButtons ? 'cursor-not-allowed' : 'cursor-pointer')}`}
                onClick={!isSaving ? handleSaveClick : undefined}
                disabled={disableButtons}
            >
                {isSaving ? (
                    <Spinner size="h-5 w-5" timer={false} />
                ) : (
                    <CheckCircleIcon className="h-6 w-6" />
                )}
            </VetRecButton>}
            {editing && !isSaving && !disabled && <button
                className={`flex flex-row rounded-full hover:text-red-600 h-6 w-6 items-center justify-center cursor-pointer`}
                onClick={disableEditing}
            >
                <XCircleIcon className="h-6 w-6" />
            </button>}
        </div>
    );
};

export default EditableLabel;